import { useApiContext } from '@/contexts/apiContext';
import ErrorMessage from '@/components/errorMessage';
import { useNotificationContext } from '@/contexts/notificationContext';
import BuildIcon from '@mui/icons-material/Build';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import RefreshIcon from '@mui/icons-material/Refresh';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Box,
  Button,
  Chip,
  CircularProgress,
  Grid,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';

interface McpToolViewerProps {
  initialNamespace?: string;
  initialServerUrl?: string;
  initialApiKey?: string;
}

export const McpToolViewer: React.FC<McpToolViewerProps> = ({
  initialNamespace = '',
  initialServerUrl = '',
  initialApiKey = '',
}) => {
  const apiContext = useApiContext();
  const notificationContext = useNotificationContext();

  const [namespace, setNamespace] = useState<string>(initialNamespace);
  const [serverUrl, setServerUrl] = useState<string>(initialServerUrl);
  const [apiKey, setApiKey] = useState<string>(initialApiKey);

  const [loading, setLoading] = useState<boolean>(false);
  const [tools, setTools] = useState<any[]>([]);
  const [error, setError] = useState<string | null>(null);

  const [selectedTool, setSelectedTool] = useState<string | null>(null);
  const [toolArgs, setToolArgs] = useState<Record<string, string>>({});
  const [executingTool, setExecutingTool] = useState<string | null>(null);
  const [executionResults, setExecutionResults] = useState<Record<string, any>>({});

  const handleFetchTools = async () => {
    if (!namespace && !serverUrl) {
      setError('Please provide a Smithery Namespace or an MCP Server URL.');
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const fetchedTools = await apiContext.getMcpTools(
        namespace || undefined,
        serverUrl || undefined,
        apiKey || undefined,
      );
      setTools(fetchedTools);
      if (fetchedTools.length === 0) {
        notificationContext?.toast?.({ message: 'No tools found for this server or namespace.', type: 'info' });
      } else {
        notificationContext?.toast?.({
          message: `Discovered ${fetchedTools.length} MCP tools successfully.`,
          type: 'success',
        });
      }
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || 'Failed to connect to MCP server / Smithery gateway.';
      setError(msg);
    } finally {
      setLoading(false);
    }
  };

  const handleExecuteTool = async (tool: any) => {
    const toolName = tool.name;
    setExecutingTool(toolName);
    setError(null);

    let parsedArgs: Record<string, any> = {};
    const rawArgs = toolArgs[toolName] || '{}';
    try {
      parsedArgs = JSON.parse(rawArgs);
    } catch {
      setError(`Invalid JSON arguments for tool "${toolName}".`);
      setExecutingTool(null);
      return;
    }

    try {
      const result = await apiContext.callMcpTool(
        toolName,
        parsedArgs,
        namespace || undefined,
        serverUrl || undefined,
        apiKey || undefined,
      );
      setExecutionResults((prev) => ({ ...prev, [toolName]: result }));
      notificationContext?.toast?.({ message: `Tool "${toolName}" executed successfully.`, type: 'success' });
    } catch (err: any) {
      const msg = err?.response?.data?.message || err?.message || `Execution of tool "${toolName}" failed.`;
      setExecutionResults((prev) => ({
        ...prev,
        [toolName]: { isError: true, content: [{ type: 'text', text: msg }] },
      }));
    } finally {
      setExecutingTool(null);
    }
  };

  return (
    <Box sx={{ p: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
      {/* Header & Connection Controls */}
      <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: '#f8fafc' }}>
        <Typography variant="h6" fontWeight={600} gutterBottom sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <BuildIcon color="primary" /> Smithery & MCP Tool Explorer
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Connect to a Smithery Namespace (e.g. <code>my-namespace</code>) or custom MCP server URL to discover and test
          agent tools.
        </Typography>

        <Grid container spacing={2}>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Smithery Namespace"
              placeholder="e.g. github-agent or exa-search"
              value={namespace}
              onChange={(e) => setNamespace(e.target.value)}
              disabled={loading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              size="small"
              label="Or MCP Server URL"
              placeholder="https://mcp.smithery.run/my-ns/sse"
              value={serverUrl}
              onChange={(e) => setServerUrl(e.target.value)}
              disabled={loading}
            />
          </Grid>
          <Grid size={{ xs: 12, sm: 4 }}>
            <TextField
              fullWidth
              size="small"
              type="password"
              label="Smithery / MCP API Key (Optional)"
              placeholder="Bearer Token or Key"
              value={apiKey}
              onChange={(e) => setApiKey(e.target.value)}
              disabled={loading}
            />
          </Grid>
        </Grid>

        <Stack direction="row" spacing={2} sx={{ mt: 2 }} alignItems="center">
          <Button
            variant="contained"
            color="primary"
            onClick={handleFetchTools}
            disabled={loading || (!namespace && !serverUrl)}
            startIcon={loading ? <CircularProgress size={16} color="inherit" /> : <RefreshIcon />}
          >
            {loading ? 'Connecting...' : 'Discover Tools'}
          </Button>
          {tools.length > 0 && (
            <Chip
              icon={<CheckCircleOutlineIcon />}
              label={`${tools.length} Tools Available`}
              color="success"
              variant="outlined"
            />
          )}
        </Stack>
      </Paper>

      {/* Error Display */}
      {error && <ErrorMessage error={error} onClose={() => setError(null)} />}

      {/* Discovered Tools List */}
      {tools.length > 0 && (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          <Typography variant="subtitle1" fontWeight={600}>
            Discovered Tools
          </Typography>

          {tools.map((tool) => {
            const toolName = tool.name;
            const isSelected = selectedTool === toolName;
            const isExecuting = executingTool === toolName;
            const result = executionResults[toolName];

            return (
              <Accordion
                key={toolName}
                expanded={isSelected}
                onChange={() => setSelectedTool(isSelected ? null : toolName)}
                variant="outlined"
                sx={{ borderRadius: 1.5, '&:before': { display: 'none' } }}
              >
                <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                  <Box
                    sx={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      width: '100%',
                      pr: 2,
                    }}
                  >
                    <Stack direction="row" spacing={1.5} alignItems="center">
                      <Chip label={toolName} color="primary" size="small" variant="outlined" sx={{ fontWeight: 600 }} />
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{ maxWidth: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                      >
                        {tool.description || 'No description provided.'}
                      </Typography>
                    </Stack>
                    {result && (
                      <Chip
                        size="small"
                        icon={result.isError ? <ErrorOutlineIcon /> : <CheckCircleOutlineIcon />}
                        label={result.isError ? 'Error' : 'Executed'}
                        color={result.isError ? 'error' : 'success'}
                      />
                    )}
                  </Box>
                </AccordionSummary>

                <AccordionDetails sx={{ pt: 1, borderTop: '1px solid #f1f5f9' }}>
                  <Stack spacing={2}>
                    {/* Tool Schema & Parameters */}
                    <Box>
                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                        PARAMETERS (JSON SCHEMA)
                      </Typography>
                      <Paper
                        variant="outlined"
                        sx={{
                          p: 1.5,
                          mt: 0.5,
                          bgcolor: '#f8fafc',
                          fontFamily: 'monospace',
                          fontSize: '0.78rem',
                          maxHeight: 180,
                          overflow: 'auto',
                        }}
                      >
                        <pre style={{ margin: 0 }}>
                          {JSON.stringify(tool.inputSchema?.properties || tool.inputSchema || {}, null, 2)}
                        </pre>
                      </Paper>
                    </Box>

                    {/* Interactive Tool Runner */}
                    <Box>
                      <Typography variant="caption" fontWeight={700} color="text.secondary">
                        TEST ARGUMENTS (JSON)
                      </Typography>
                      <TextField
                        fullWidth
                        multiline
                        rows={3}
                        size="small"
                        placeholder='{"query": "example"}'
                        value={toolArgs[toolName] || ''}
                        onChange={(e) =>
                          setToolArgs((prev) => ({
                            ...prev,
                            [toolName]: e.target.value,
                          }))
                        }
                        sx={{ mt: 0.5, fontFamily: 'monospace', fontSize: '0.85rem' }}
                      />
                      <Button
                        variant="contained"
                        size="small"
                        color="secondary"
                        onClick={() => handleExecuteTool(tool)}
                        disabled={isExecuting}
                        startIcon={isExecuting ? <CircularProgress size={14} color="inherit" /> : <PlayArrowIcon />}
                        sx={{ mt: 1 }}
                      >
                        {isExecuting ? 'Executing...' : 'Run Tool'}
                      </Button>
                    </Box>

                    {/* Output Viewer */}
                    {result && (
                      <Box>
                        <Typography
                          variant="caption"
                          fontWeight={700}
                          color={result.isError ? 'error.main' : 'success.main'}
                        >
                          {result.isError ? 'EXECUTION ERROR' : 'EXECUTION OUTPUT'}
                        </Typography>
                        <Paper
                          variant="outlined"
                          sx={{
                            p: 1.5,
                            mt: 0.5,
                            bgcolor: result.isError ? '#fff1f2' : '#f0fdf4',
                            border: result.isError ? '1px solid #fecdd3' : '1px solid #bbf7d0',
                            maxHeight: 250,
                            overflow: 'auto',
                            fontFamily: 'monospace',
                            fontSize: '0.8rem',
                          }}
                        >
                          {result.content?.map((item: any, idx: number) => (
                            <Box key={idx} sx={{ mb: 1 }}>
                              {item.type === 'text' && (
                                <Typography variant="body2" sx={{ fontFamily: 'inherit' }}>
                                  {item.text}
                                </Typography>
                              )}
                              {item.type === 'image' && (
                                <img
                                  src={`data:${item.mimeType || 'image/png'};base64,${item.data}`}
                                  alt="Tool output"
                                  style={{ maxWidth: '100%', borderRadius: 4 }}
                                />
                              )}
                            </Box>
                          )) || <pre style={{ margin: 0 }}>{JSON.stringify(result, null, 2)}</pre>}
                        </Paper>
                      </Box>
                    )}
                  </Stack>
                </AccordionDetails>
              </Accordion>
            );
          })}
        </Box>
      )}
    </Box>
  );
};

export default McpToolViewer;
